import React, { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import Footer from '../components/common/Footer'
import authService from '../services/authService'
import { 
  HomeIcon, 
  UsersIcon, 
  CalendarIcon, 
  ChartBarIcon, 
  CogIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  ClipboardDocumentCheckIcon,
  QueueListIcon,
  ChatBubbleLeftRightIcon,
  TvIcon,
  AdjustmentsHorizontalIcon,
  ClockIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

const AdminLayout: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [currentTime, setCurrentTime] = useState(new Date())
  const { user, logout, isSuperAdmin, isInterviewer, isTenantAdmin } = useAuth()
  
  // Plan data state
  const [plan, setPlan] = useState<any>(null)
  const [planLoading, setPlanLoading] = useState(true)

  // Check if current page should hide sidebar (interview page)
  const shouldHideSidebar = location.pathname === '/admin/interview'

  // Get user role
  const userRole = (user as any)?.role || 'tenant_admin'

  // Load plan data
  useEffect(() => {
    const loadPlanData = async () => {
      try {
        setPlanLoading(true)
        // First try to get from stored data
        const storedPlan = authService.getStoredPlan()
        if (storedPlan) {
          setPlan(storedPlan)
          setPlanLoading(false)
          return
        }
        
        // If no stored data, fetch from API
        const userData = await authService.getCurrentUser()
        const freshPlan = authService.getStoredPlan()
        if (freshPlan) {
          setPlan(freshPlan)
        }
      } catch (error) {
        console.error('Error loading plan data:', error)
        // Set default plan if API fails
        setPlan({ name: 'Starter', status: 'active', is_trial_active: false, plan_status: 'active' })
      } finally {
        setPlanLoading(false)
      }
    }

    loadPlanData()
  }, [])

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Navigation based on user role
  const getNavigation = () => {
    if (userRole === 'interviewer') {
      return [
        { name: 'Dashboard', href: '/admin/dashboard', icon: HomeIcon },
        { name: 'My Queue', href: '/admin/interviewer-live-queue', icon: QueueListIcon },
        { name: 'Submit Feedback', href: '/admin/submit-feedback', icon: ChatBubbleLeftRightIcon },
        { name: 'Candidates', href: '/admin/candidates', icon: UsersIcon },
      ]
    }

    // Admin and Drive Manager navigation
    return [
      { name: 'Dashboard', href: '/admin/dashboard', icon: HomeIcon },
      { name: 'Setup Wizard', href: '/admin/drive-setup-wizard', icon: SparklesIcon },
      { name: 'Drives', href: '/admin/drives', icon: CalendarIcon },
      { name: 'Check-in', href: '/admin/check-in', icon: ClipboardDocumentCheckIcon },
      { name: 'Live Queue', href: '/admin/live-queue', icon: QueueListIcon },
      { name: 'Feedback', href: '/admin/feedback', icon: ChatBubbleLeftRightIcon },
      { name: 'Candidates', href: '/admin/candidates', icon: UsersIcon },
      { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon },
      ...(isSuperAdmin() ? [{ name: 'Tenants', href: '/admin/tenants', icon: BuildingOfficeIcon }] : []),
      { name: 'Settings', href: '/admin/settings', icon: CogIcon },
    ]
  }

  const navigation = getNavigation()

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
      // Force logout by clearing local storage
      localStorage.clear()
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      {!shouldHideSidebar && (
        <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg">
          <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200">
              <Link to="/admin/dashboard" className="flex items-center">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xl">D</span>
                </div>
                <span className="ml-3 text-2xl font-bold text-gray-900">DriveManager</span>
              </Link>
            </div>

            {/* User Role Badge */}
            {isSuperAdmin() && (
              <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                <span className="text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded">
                  Super Administrator
                </span>
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>

            {/* Removed user profile from sidebar - moved to header */}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={shouldHideSidebar ? 'pl-0' : 'pl-64'}>
        {/* Top Bar */}
        {!shouldHideSidebar && (
          <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="px-6 py-3">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold text-gray-900">
                  {location.pathname === '/admin/submit-feedback' ? 'Submit Feedback' : 
                   navigation.find(item => item.href === location.pathname)?.name || 'Admin Panel'}
                </h1>
                
                <div className="flex items-center space-x-3">
                  {/* Show Plan details only to tenant admins */}
                  {isTenantAdmin() && (
                    <>
                      {/* Plan Information */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Plan:</span>
                        {planLoading ? (
                          <span className="text-xs text-gray-400">Loading...</span>
                        ) : plan ? (
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            plan.is_trial_active 
                              ? 'text-orange-600 bg-orange-50' 
                              : plan.plan_status === 'active'
                              ? 'text-green-600 bg-green-50'
                              : 'text-red-600 bg-red-50'
                          }`}>
                            {plan.name}
                            {plan.is_trial_active && ' (Trial)'}
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-gray-600 bg-gray-50 px-2 py-1 rounded-full">
                            Free
                          </span>
                        )}
                      </div>

                      {/* Lobby Display Button */}
                      <Link 
                        to="/admin/lobby-display"
                        className="flex items-center px-2 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        <TvIcon className="w-3.5 h-3.5 mr-1.5" />
                        <span className="text-xs font-medium">Lobby Display</span>
                      </Link>

                      {/* Setup Status */}
                      <div className="flex items-center space-x-1.5">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-xs text-gray-600">Setup</span>
                      </div>
                    </>
                  )}

                  {/* Current Time */}
                  <div className="flex items-center space-x-1.5">
                    <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600">
                      {currentTime.toLocaleTimeString('en-US', { 
                        hour12: true, 
                        hour: '2-digit', 
                        minute: '2-digit', 
                        second: '2-digit' 
                      })}
                    </span>
                  </div>

                  <button className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
                    <BellIcon className="w-4 h-4" />
                  </button>

                  {/* User Profile - Moved from sidebar to header */}
                  <div className="flex items-center space-x-3 border-l border-gray-200 pl-3">
                    <div className="flex items-center space-x-2">
                      <UserCircleIcon className="w-6 h-6 text-gray-400" />
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-700">
                          {user?.name || 'Loading...'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.email || 'Loading...'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Logout"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Page Content */}
        <main className={shouldHideSidebar ? 'p-0 pb-16' : 'p-6 pb-20'}>
          <Outlet />
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  )
}

export default AdminLayout
